"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resetClientLoggerForTests = resetClientLoggerForTests;
exports.createAppLogger = createAppLogger;
const client_serialization_1 = require("./client-serialization");
const newrelic_browser_transport_1 = require("./newrelic-browser-transport");
const telemetry_transport_1 = require("./telemetry-transport");
const loggers = new Map();
let defaultTransport;
function resolveTransport() {
    const configured = (0, telemetry_transport_1.getConfiguredTelemetryTransport)();
    if (configured !== undefined) {
        return configured;
    }
    if (defaultTransport === undefined) {
        defaultTransport = (0, newrelic_browser_transport_1.createNewRelicBrowserTransport)();
    }
    return defaultTransport;
}
function resetClientLoggerForTests() {
    loggers.clear();
    defaultTransport = undefined;
}
function getConsoleWriter(level) {
    switch (level) {
        case "trace":
            return (message, payload) => {
                if (typeof console.trace === "function") {
                    console.trace(message, payload);
                    return;
                }
                console.debug(message, payload);
            };
        case "debug":
            return (message, payload) => console.debug(message, payload);
        case "info":
            return (message, payload) => console.info(message, payload);
        case "warn":
            return (message, payload) => console.warn(message, payload);
        case "error":
        case "fatal":
            return (message, payload) => console.error(message, payload);
    }
}
function emitToTransport(transport, level, message, attributes, error) {
    const correlation = transport.getCorrelationMetadata?.();
    const enrichedAttributes = (0, client_serialization_1.mergeCorrelationMetadata)(attributes, correlation);
    if (level === "error" || level === "fatal") {
        const resolvedError = error ?? new Error(message);
        transport.log("error", message, enrichedAttributes);
        transport.noticeError(resolvedError, enrichedAttributes);
        transport.captureException?.(resolvedError, enrichedAttributes);
        return;
    }
    transport.log(level, message, enrichedAttributes);
}
function emitLog(level, message, loggerName, data) {
    if (!(0, client_serialization_1.shouldEmitLogLevel)(level)) {
        return;
    }
    const attributes = (0, client_serialization_1.normalizeClientAttributes)(loggerName, message, data);
    const consoleWriter = getConsoleWriter(level);
    consoleWriter(message, attributes);
    const transport = resolveTransport();
    if (!transport) {
        return;
    }
    try {
        if (level === "error" || level === "fatal") {
            const telemetryEventId = (0, client_serialization_1.generateTelemetryEventId)();
            const error = (0, client_serialization_1.resolveErrorForEmission)(message, data);
            const errorAttributes = {
                ...attributes,
                telemetryEventId,
            };
            emitToTransport(transport, level, message, errorAttributes, error);
            return;
        }
        emitToTransport(transport, level, message, attributes);
    }
    catch {
        // Vendor transport failures must never break application rendering.
    }
}
function createClientLogger(loggerName) {
    return {
        trace(message, data) {
            emitLog("trace", message, loggerName, data);
        },
        debug(message, data) {
            emitLog("debug", message, loggerName, data);
        },
        info(message, data) {
            emitLog("info", message, loggerName, data);
        },
        warn(message, data) {
            emitLog("warn", message, loggerName, data);
        },
        error(message, data) {
            emitLog("error", message, loggerName, data);
        },
        fatal(message, data) {
            emitLog("fatal", message, loggerName, data);
        },
        startTiming(timingName) {
            const startTime = Date.now();
            return () => {
                emitLog("info", "Timing complete", loggerName, {
                    duration: Date.now() - startTime,
                    name: timingName,
                    tag: "timing_complete",
                });
            };
        },
        time(timingName, fn) {
            const endTiming = this.startTiming(timingName);
            const result = fn();
            endTiming();
            return result;
        },
        async timeAsync(timingName, fn) {
            const endTiming = this.startTiming(timingName);
            const result = await fn();
            endTiming();
            return result;
        },
    };
}
function createAppLogger({ name }) {
    const existingLogger = loggers.get(name);
    if (existingLogger) {
        return existingLogger;
    }
    const logger = createClientLogger(name);
    loggers.set(name, logger);
    return logger;
}
//# sourceMappingURL=app-logger.client.js.map